import { useNavigate } from '@tanstack/react-router';
import type { CollectionItemTreeNode } from '@/types/article-collection';
import FileTextIcon from '@/assets/common/file-text.svg?react';
import FolderTreeIcon from '@/assets/common/folder-tree.svg?react';
import ChevronDownIcon from '@/assets/common/chevron-down.svg?react';
import ChevronRightIcon from '@/assets/common/chevron-right.svg?react';
import PlusIcon from '@/assets/common/plus.svg?react';
import TrashIcon from '@/assets/common/trash.svg?react';
import styles from './CollectionItemList.module.scss';
import { useState } from 'react';

interface CollectionItemListProps {
  items: CollectionItemTreeNode[];
  collectionId: string;
  hasReadingModeAccess: boolean;
  onReorder: (items: Array<{ id: string; parent_id: string | null; order_index: number }>) => void;
  onDeleteItem: (itemId: string, title: string) => void;
  onAddHeading: (parentId: string | null) => void;
  onAddArticles: (parentId: string | null) => void;
}

interface CollectionItemProps {
  item: CollectionItemTreeNode;
  collectionId: string;
  hasReadingModeAccess: boolean;
  onDeleteItem: (itemId: string, title: string) => void;
  onAddHeading: (parentId: string | null) => void;
  onAddArticles: (parentId: string | null) => void;
}

function CollectionItemRow({
  item,
  collectionId,
  hasReadingModeAccess,
  onDeleteItem,
  onAddHeading,
  onAddArticles,
}: CollectionItemProps) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);

  const isHeading = item.item_type === 'heading';
  const hasChildren = item.children.length > 0;

  const handleClick = () => {
    if (isHeading) {
      setIsExpanded(!isExpanded);
    } else if (item.article_id) {
      if (hasReadingModeAccess) {
        navigate({
          to: '/articles/collections/$collectionId/articles/$articleId',
          params: { collectionId, articleId: item.article_id },
        });
      } else {
        navigate({
          to: '/articles/$articleId',
          params: { articleId: item.article_id },
        });
      }
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    const title = isHeading ? item.title || 'Untitled' : item.article_title || 'Untitled';
    onDeleteItem(item.id, title);
  };

  const handleAddHeadingInside = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddHeading(item.id);
  };

  const handleAddArticlesInside = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddArticles(item.id);
  };

  return (
    <div className={styles.collectionItem}>
      <div
        className={`${styles.collectionItem__row} ${isHeading ? styles['collectionItem__row--heading'] : ''}`}
        style={{ paddingLeft: `${item.depth * 1.5 + 0.5}rem` }}
        onClick={handleClick}
      >
        {isHeading && (
          <button className={styles.collectionItem__expand}>
            {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </button>
        )}

        <div className={styles.collectionItem__icon}>
          {isHeading ? <FolderTreeIcon /> : <FileTextIcon />}
        </div>

        <div className={styles.collectionItem__content}>
          <span className={styles.collectionItem__title}>
            {isHeading ? item.title : item.article_title || 'Untitled'}
          </span>
          {!isHeading && item.time_to_read_minutes && (
            <span className={styles.collectionItem__meta}>
              {item.time_to_read_minutes} min read
            </span>
          )}
        </div>

        <div className={styles.collectionItem__actions}>
          {isHeading && (
            <>
              <button
                className={styles.collectionItem__actionButton}
                onClick={handleAddHeadingInside}
                title="Add sub-heading"
              >
                <FolderTreeIcon />
                <PlusIcon className={styles.collectionItem__actionPlus} />
              </button>
              <button
                className={styles.collectionItem__actionButton}
                onClick={handleAddArticlesInside}
                title="Add articles inside"
              >
                <FileTextIcon />
                <PlusIcon className={styles.collectionItem__actionPlus} />
              </button>
            </>
          )}
          <button
            className={`${styles.collectionItem__actionButton} ${styles['collectionItem__actionButton--danger']}`}
            onClick={handleDelete}
            title={isHeading ? 'Delete heading' : 'Remove from collection'}
          >
            <TrashIcon />
          </button>
        </div>
      </div>

      {isHeading && isExpanded && hasChildren && (
        <div className={styles.collectionItem__children}>
          {item.children.map((child) => (
            <CollectionItemRow
              key={child.id}
              item={child}
              collectionId={collectionId}
              hasReadingModeAccess={hasReadingModeAccess}
              onDeleteItem={onDeleteItem}
              onAddHeading={onAddHeading}
              onAddArticles={onAddArticles}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CollectionItemList({
  items,
  collectionId,
  hasReadingModeAccess,
  onDeleteItem,
  onAddHeading,
  onAddArticles,
}: CollectionItemListProps) {
  return (
    <div className={styles.collectionItemList}>
      {items.map((item) => (
        <CollectionItemRow
          key={item.id}
          item={item}
          collectionId={collectionId}
          hasReadingModeAccess={hasReadingModeAccess}
          onDeleteItem={onDeleteItem}
          onAddHeading={onAddHeading}
          onAddArticles={onAddArticles}
        />
      ))}
    </div>
  );
}
